import { useMemo } from 'react';
import { useLocations } from 'state/locations';
import { useUserPermittedCities } from 'state/userPermissions';
import { METROPOLITAN_CODE_TO_LABEL } from 'utils/constants/location';

/**
 * A region is only usable if we have a label for it. Regions without a label
 * aren't supported downstream (e.g. ElasticSearch), so we drop them here at the
 * source so they never surface in any region selector or filter.
 */
const isSupportedRegion = (regionCode: string) =>
  regionCode in METROPOLITAN_CODE_TO_LABEL;

type City = {
  country: string;
  state: string;
  countyId: string | null;
  countyName: string;
  id: number;
  name: string;
  code: string;
};

type Region = {
  country: string;
  cities: City[];
  counties: Record<string, { name: string; cities: City[] }>;
};

/**
 * Handles which cities, and regions are included in the user's subscription plan
 */
export const useAvailableCities = () => {
  const [userPermittedCities] = useUserPermittedCities();
  const { filteredCities, cityToRegionMap } = useLocations();

  const availableCities = useMemo(() => {
    return filteredCities.filter(({ id }) => userPermittedCities?.includes(id));
  }, [filteredCities, userPermittedCities]);

  const availableRegions = useMemo(() => {
    const regionObject: Record<string, Region> = {};

    availableCities.forEach((city) => {
      const regionCode = cityToRegionMap[city.id];
      if (regionCode && isSupportedRegion(regionCode)) {
        if (!regionObject[regionCode]) {
          regionObject[regionCode] = {
            country: city.country,
            cities: [],
            counties: {},
          };
        }

        regionObject[regionCode].cities.push(city);
        const countyKey = city.countyId || 'unknown';
        if (!regionObject[regionCode].counties[countyKey]) {
          regionObject[regionCode].counties[countyKey] = {
            name: city.countyName || countyKey,
            cities: [],
          };
        }
        regionObject[regionCode].counties[countyKey].cities.push(city);
      }
    });

    return regionObject;
  }, [availableCities, cityToRegionMap]);

  const availableStates = useMemo(() => {
    const stateObject: Record<
      string,
      { country: string; regions: Record<string, Region> }
    > = {};

    availableCities.forEach((city) => {
      const stateCode = city.state;
      const regionCode = cityToRegionMap[city.id];

      if (stateCode && regionCode && isSupportedRegion(regionCode)) {
        if (!stateObject[stateCode]) {
          stateObject[stateCode] = {
            country: city.country,
            regions: {},
          };
        }

        if (!stateObject[stateCode].regions[regionCode]) {
          stateObject[stateCode].regions[regionCode] = {
            country: city.country,
            cities: [],
            counties: {},
          };
        }

        stateObject[stateCode].regions[regionCode].cities.push(city);
        const countyKey = city.countyId || 'unknown';
        if (!stateObject[stateCode].regions[regionCode].counties[countyKey]) {
          stateObject[stateCode].regions[regionCode].counties[countyKey] = {
            name: city.countyName || countyKey,
            cities: [],
          };
        }
        stateObject[stateCode].regions[regionCode].counties[countyKey].cities.push(city);
      }
    });

    return stateObject;
  }, [availableCities, cityToRegionMap]);

  return { availableCities, availableRegions, availableStates };
};
